import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';

// API/Components
import axiosInstance from '../components/axios';
import DataViewSwitch from '../components/data_view_switch';
import DischargeEventsList from '../components/discharge_events_list';
import DischargeEventsCalendar from '../components/discharge_events_calendar';

// Material UI
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';


// System Trend sub-page rendering (Location Name, Title)
export default function DischargeEvents() {
	const { locationNum } = useParams();
	const [ searchParams ] = useSearchParams();

	// State Management
	const [locationInfo, setLocationInfo] = useState({locationName: ''});
	const [eventsData, setEventsData] = useState([]);
	
	const [isLoading, setIsLoading] = useState(true);
	const [calendarDisplay, setCalendarDisplay] = useState(true);
	const [year, setYear] = useState(new Date().getFullYear());
	
	const currentYear = new Date().getFullYear();

	// Get information about the location id specified (name, size, num of temps)
	useEffect(() => {
		axiosInstance.get(`/loc/${locationNum}/size/`).then(response => {
			const locationInfo = response.data;
			setLocationInfo({
				locationName: locationInfo.location_name,
				serialNumber: locationInfo.serial_number,
			});
		});
	}, [locationNum]);
	
	// Get the list of trend data for the specified location id
	useEffect(() => {
		setIsLoading(true);

		// Get System Trend Data
		axiosInstance.get(`/loc/${locationNum}/discharges`).then(response => {				
			const eventsData = response.data;
			setEventsData(eventsData);		
		});

		setIsLoading(false);
	}, []);

	// Event Handlers
	const handleViewChange = (event) => {
		setCalendarDisplay(event.target.checked);
	}
		
	return (
		<Paper
			padding='true'
			sx={{
				overflow: 'auto',
				boxShadow: '0',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Typography
				variant='h5'
				fontFamily='Saira, sans-serif'
				fontWeight='600'
				color='#0F1820'
				align='center'
				padding='5px'
			>
				{ locationInfo.locationName } (SN {locationInfo.serialNumber})
			</Typography>
			<Toolbar>
				<Grid container justifyContent='left'>
					<Grid item>
						<Typography
							align="left"
							fontFamily='Saira,sans-serif'
							fontWeight='600'
							sx={{ flexGrow: 1}}
						>
							Discharge/Charge Events
						</Typography>
					</Grid>
				</Grid>
				{ calendarDisplay && // Only show the year selector if the calendar view is selected
					<Grid container spacing={1} alignItems='center' justifyContent='center' sx={{ maxWidth: '100%', overflow: 'hidden' }}>
						<Grid item sx={{ flexShrink: 1 }}>
							<IconButton
								variant='contained'
								onClick={() => setYear(year - 1)}
								sx={{
									pl: 1,
									pr: 0,
									height: '100%',
									width: '100%',
									maxWidth: '22px',
									maxHeight: '22px',
									borderRadius: '5px',
									border: '1px solid',
									align: 'center',
									bgcolor: '#B1282E',
									'&:hover': {
										bgcolor: "#B1282E77"
									},
								}}
							>
								<ArrowBackIosIcon sx={{ fontSize: '0.9em', color: 'white' }} />
							</IconButton>
						</Grid>
						<Grid item sx={{ flexShrink: 1, display: 'flex' }}>
							<Typography
								fontFamily='Saira,sans-serif'
								fontWeight='600'
								fontSize='1.3em'
								sx={{ flexGrow: 1 }}
							>
								{year}
							</Typography>
						</Grid>
						<Grid item sx={{ flexShrink: 1 }}>
							<IconButton
								variant='contained'
								onClick={() => setYear(year + 1)}
								disabled={year === currentYear}
								sx={{
									pl: 1,
									pr: 1,
									height: '100%',
									width: '100%',
									maxWidth: '22px',
									maxHeight: '22px',
									borderRadius: '5px',
									border: '1px solid',
									align: 'center',
									bgcolor: '#B1282E',
									'&:hover': {
										bgcolor: "#B1282E77"
									},
								}}
							>
								<ArrowForwardIosIcon sx={{ fontSize: '0.9em', color: 'white' }} />
							</IconButton>
						</Grid>
					</Grid>
				}
				<Grid container spacing={1} justifyContent='right'>
					<Grid item>
						<DataViewSwitch
							checked={calendarDisplay}
							onChange={handleViewChange}
							disabled={isLoading}
							leftText="Report"
							rightText="Calendar"
						/>
					</Grid>
				</Grid>
			</Toolbar>
			{ calendarDisplay ?
				<DischargeEventsCalendar
					locationID={locationNum}
					eventsData={eventsData}
					selectedYear={year}
					loading={isLoading}
				/>
			:
				<DischargeEventsList
					locationID={locationNum}
					eventsData={eventsData}
					loading={isLoading}
				/>
			}
		</Paper>
	);
}